import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, Award, Target, Compass, Sparkles, Heart, Quote, Factory, Shield, Sun, Cpu, Zap, Pause, Play, ChevronRight, Building2 } from 'lucide-react';
import { COMPANY, STATS, FOCUS_AREAS } from '../data/content';
import PerformanceCarousel from '../components/PerformanceCarousel';
import { PERFORMANCE_TILES } from '../data/performanceTiles';
import CountUp from '../components/CountUp';

// Expanded milestone set so the journey feels rich. Curve fits 7 points.
const MILESTONES = [
  { y: '1929',  t: 'Trilok Chand Group',              d: 'The family enters the electrical industry — laying the foundation of a century-long legacy in India.',                                                  icon: Sparkles },
  { y: '1989',  t: 'Tricolite Electrical Industries', d: 'Manufacturing of low-voltage and medium-voltage switchboards begins. "Tricolite" becomes a recognised quality standard in Indian electrical hardware.', icon: Factory,  logo: '/logos/tricolite-electrical.png'  },
  { y: '2012',  t: 'Tricolite Energy Solutions',      d: 'The EPC arm is established in Gurugram — a dedicated company for affordable, clean and efficient electrical & solar projects across India.',          icon: Target,  logo: '/logos/full.png'  },
  { y: '2017',  t: 'ISO Certified',          d: 'Certified ISO 9001 (Quality), 14001 (Environment) & 45001 (Safety) by URS, India — international standards baked into every Tricolite project.',       icon: Shield   },
  { y: '2019',  t: 'Solar EPC Scale-Up',              d: 'Crossed 1 MWp of solar rooftops commissioned. Rolled out ground-mounted Solar EPC capabilities (AC + DC) across multiple states.',                     icon: Sun      },
  { y: '2022',  t: 'IoT-Driven Efficiency',           d: 'Launched Energy Audits and IoT-based monitoring solutions — bringing real-time efficiency analytics to client substations and rooftops.',              icon: Cpu      },
  { y: '2024+', t: '720 MINR+ Scale',                 d: 'A 720 MINR+ technology-oriented EPC company. 370 MVA substations commissioned, growing footprint across automotive, hospitality, infra and data centre.', icon: Zap      },
];

const PLEDGE = [
  { text: 'We are all one team connected with each other with ', hl: 'love and respect', after: '.' },
  { text: 'We are an army that is ', hl: 'courageous and disciplined', after: '.' },
  { text: 'We are always ready to fight all our ', hl: 'internal impedencies and external challenges', after: '.' },
  { text: 'We are committed to a life of ', hl: 'total integrity, service & sacrifice', after: ' which will be beneficial to our nation.' },
  { text: 'We have the ability to ', hl: 'produce more than that we consume and give more than we take', after: '.' },
  { text: 'We have the determination to follow a path of ', hl: 'wisdom, righteousness and courage', after: '.' },
  { text: 'We are fully aware of our ', hl: 'responsibilities', after: ' and pray the lord gives us the ability to fulfil them.' },
];

const TEAM = [
  { name: 'Manoj Sharma',    role: 'Finance',                  email: 'finance@tricoliteenergy.in',         img: '/team/manoj.webp' },
  { name: 'Kaushal Jha',     role: 'MEP Solutions',            email: 'kaushal.jha@tricoliteenergy.in',     img: '/team/kaushal.webp' },
  { name: 'Waseem Ahmed',    role: 'Construction',             email: 'waseem.ahmed@tricoliteenergy.in',    img: '/team/waseem.webp' },
  { name: 'Priyanka Hasija', role: 'Customer Care Comrade',    email: 'priyanka.hasija@tricoliteenergy.in', img: '/team/priyanka.webp' },
  { name: 'Madan Bairwa',    role: 'HR & Admin',               email: 'madan.bairwa@tricoliteenergy.in',    img: '/team/madan.webp' },
  { name: 'Sagar Sharma',    role: 'Procurement',              email: 'sagar.sharma@tricoliteenergy.in',    img: '/team/sagar.webp' },
];

// 7 evenly-spaced curve points across the viewBox (0–1200 x 0–260)
const CURVE_POINTS = [
  { x:   80, y: 180 },
  { x:  240, y:  70 },
  { x:  400, y: 170 },
  { x:  600, y:  60 },
  { x:  800, y: 170 },
  { x:  960, y:  70 },
  { x: 1120, y: 180 },
];
const CURVE_D = 'M 80,180 C 140,80 200,40 240,70 C 280,100 360,180 400,170 C 460,160 540,60 600,60 C 660,60 740,160 800,170 C 860,180 900,100 960,70 C 1020,40 1080,100 1120,180';

export default function About() {
  const [active, setActive] = useState(2);
  const [autoPlay, setAutoPlay] = useState(true);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const hoverRef = useRef(false);

  const onHeroLoad = useCallback(() => setHeroLoaded(true), []);

  // Auto-advance every 4.5s unless paused/hovered
  useEffect(() => {
    if (!autoPlay) return;
    const id = setInterval(() => {
      if (!hoverRef.current) setActive((p) => (p + 1) % MILESTONES.length);
    }, 4500);
    return () => clearInterval(id);
  }, [autoPlay]);

  return (
    <main>
      {/* HERO — banner image as background */}
      <section id="who-we-are" className="relative overflow-hidden" style={{ height: '120svh', minHeight: '700px' }} data-testid="about-hero" data-nav-theme="dark">
        {/* Shimmer skeleton — visible until hero image loads */}
        <div className={`absolute inset-0 bg-[var(--ink)] transition-opacity duration-700 ${heroLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <div className="absolute inset-0 shimmer-bg"/>
        </div>
        <img src="/img/about-hero.jpg" alt="Tricolite Energy" loading="eager" decoding="sync" fetchPriority="high" onLoad={onHeroLoad} className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}/>
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)] from-15% via-[var(--ink)]/75 via-50% to-[var(--ink)]/30"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/60 via-transparent to-[var(--ink)]/20"/>
        <div className="absolute inset-0 noise opacity-20 pointer-events-none"/>

        <div className="relative h-full px-6 md:px-10 lg:px-14 max-w-[1500px] mx-auto flex flex-col justify-center pt-[84px]">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/25 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--orange)]"/>
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-white">About Us</span>
            </div>
            <h1 className="font-display text-[clamp(40px,5.6vw,80px)] leading-[1.04] font-bold tracking-tight" style={{color:'#fff'}}>
              Built on purpose,<br/>
              <span className="font-serif-it text-[var(--blue)]">engineered to last.</span>
            </h1>
            <p className="mt-7 text-[16px] md:text-[18px] text-white max-w-xl leading-[1.7] font-medium">
              Since 2012, the EPC arm of Tricolite Electrical Industries Limited — part of the Trilok Chand Group&apos;s century-long legacy in the electrical business. We are your long-term industry partner for all electric-energy conservation needs.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
              {STATS.slice(0,3).map((s, i) => (
                <div key={i} className="border-l-2 border-[var(--blue)] pl-4">
                  <div className="font-display font-bold text-[24px] md:text-[28px] leading-none" style={{color:'#fff'}}><CountUp end={s.value} /><span className="text-[var(--blue)] text-[12px] ml-1">{s.suffix}</span></div>
                  <div className="text-[10px] uppercase tracking-wider text-white/75 mt-2 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-12 bg-white/50 animate-pulse"/>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">Scroll</span>
        </div>
      </section>

      {/* CURVED INTERACTIVE TIMELINE — auto-advances, compact, fits in viewport */}
      <section
        id="legacy"
        className="px-6 md:px-10 lg:px-14 py-20 lg:py-24 bg-[var(--cream)] relative overflow-hidden"
        data-testid="journey-section"
        onMouseEnter={() => { hoverRef.current = true; }}
        onMouseLeave={() => { hoverRef.current = false; }}
      >
        <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none"/>
        <div className="absolute top-1/3 -left-40 w-[450px] h-[450px] rounded-full bg-[var(--orange)]/6 blur-3xl pointer-events-none"/>
        <div className="absolute bottom-0 -right-40 w-[450px] h-[450px] rounded-full bg-[var(--blue)]/6 blur-3xl pointer-events-none"/>

        <div className="relative max-w-[1500px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10 reveal">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-4">Journey Over the Years</div>
              <h2 className="font-display text-[clamp(32px,4.2vw,60px)] leading-[1.02] font-bold tracking-tight">
                A <span className="text-[var(--blue)]">century</span> in <span className="font-serif-it">electrical.</span>
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAutoPlay(p => !p)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] bg-white text-[12px] uppercase tracking-wider font-semibold hover:border-[var(--orange)] hover:text-[var(--blue)] transition-colors"
                data-testid="timeline-autoplay-toggle"
              >
                {autoPlay ? <><Pause className="w-3.5 h-3.5"/> Pause</> : <><Play className="w-3.5 h-3.5"/> Play</>}
              </button>
              <div className="text-[12px] text-[var(--text-mid)]">
                <span className="font-display font-bold text-[var(--blue)]">{String(active+1).padStart(2,'0')}</span>
                <span className="mx-1.5">/</span>
                <span>{String(MILESTONES.length).padStart(2,'0')}</span>
              </div>
            </div>
          </div>

          {/* Curve + labels — one cohesive visual block */}
          <div className="relative">
            <svg viewBox="0 0 1200 260" className="w-full h-auto" preserveAspectRatio="xMidYMid meet" data-testid="timeline-curve">
              <path d={CURVE_D} fill="none" stroke="rgba(14,22,32,0.15)" strokeWidth="2" strokeDasharray="5 7"/>
              <path
                d={CURVE_D}
                fill="none"
                stroke="var(--orange)"
                strokeWidth="3.5"
                strokeLinecap="round"
                pathLength="100"
                style={{
                  strokeDasharray: 100,
                  strokeDashoffset: 100 - ((active + 1) / MILESTONES.length) * 100,
                  transition: 'stroke-dashoffset 1s cubic-bezier(.22,.9,.18,1)',
                }}
              />
              {MILESTONES.map((m, i) => {
                const p = CURVE_POINTS[i];
                const isActive = i === active;
                const isPast = i < active;
                const labelAbove = p.y < 120;
                return (
                  <g
                    key={i}
                    className="cursor-pointer"
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    data-testid={`milestone-node-${m.y === '1929' ? '1920s' : m.y}`}
                  >
                    {isActive && (
                      <circle cx={p.x} cy={p.y} r="32" fill="var(--orange)" opacity="0.15">
                        <animate attributeName="r" values="28;38;28" dur="2.4s" repeatCount="indefinite"/>
                        <animate attributeName="opacity" values="0.2;0.05;0.2" dur="2.4s" repeatCount="indefinite"/>
                      </circle>
                    )}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isActive ? 22 : 16}
                      fill={isActive ? 'var(--orange)' : isPast ? 'var(--blue)' : '#ffffff'}
                      stroke={isActive ? 'var(--orange)' : isPast ? 'var(--blue)' : 'rgba(14,22,32,0.2)'}
                      strokeWidth="2"
                      style={{ transition:'all .55s cubic-bezier(.22,.9,.18,1)', filter: isActive ? 'drop-shadow(0 6px 16px rgba(243,118,58,0.5))' : 'none' }}
                    />
                    <text x={p.x} y={p.y + 5} textAnchor="middle" fontFamily="Syne, sans-serif" fontWeight="700" fontSize="11" fill={isActive || isPast ? '#fff' : '#0E1620'}>{m.y}</text>
                    <text
                      x={p.x}
                      y={labelAbove ? p.y - 38 : p.y + 50}
                      textAnchor="middle"
                      fontFamily="Inter, sans-serif"
                      fontSize="11"
                      fontWeight="600"
                      letterSpacing="0.06em"
                      fill={isActive ? 'var(--orange)' : 'rgba(14,22,32,0.55)'}
                      style={{ textTransform:'uppercase', transition:'fill .4s' }}
                    >
                      {m.t}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Detail panel — compact, sits right under curve */}
            <div className="mt-6 bg-white border border-[var(--line)] rounded-3xl p-7 md:p-9 relative overflow-hidden shadow-[0_24px_50px_-25px_rgba(14,22,32,0.18)]">
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[var(--orange)]/10 blur-3xl"/>
              <div className="relative grid lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-3 flex items-center gap-4">
                  {MILESTONES[active].logo ? (
                    <div className="shrink-0 h-16 flex items-center">
                      <img
                        src={MILESTONES[active].logo}
                        alt={MILESTONES[active].t}
                        className="max-h-[56px] max-w-[180px] w-auto object-contain"
                        data-testid="timeline-milestone-logo"
                      />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-[var(--orange)]/12 flex items-center justify-center shrink-0">
                      {React.createElement(MILESTONES[active].icon, { className: 'w-7 h-7 text-[var(--blue)]', strokeWidth: 2 })}
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--blue)] font-semibold mb-1">Milestone</div>
                    <div className="font-display font-bold text-[clamp(32px,4vw,52px)] leading-none">{MILESTONES[active].y}</div>
                  </div>
                </div>
                <div className="lg:col-span-9">
                  <h3 className="font-display font-bold text-[clamp(20px,2.2vw,28px)] leading-tight mb-3">{MILESTONES[active].t}</h3>
                  <p className="text-[15px] md:text-[16px] text-[var(--text-mid)] leading-[1.8]">{MILESTONES[active].d}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES, VISION, MISSION & STANDARDS — 4-tile layout, ordered as Values → Vision → Mission → Standards */}
      <section id="principles" className="px-6 md:px-10 lg:px-14 py-24 lg:py-32 bg-[var(--ink)] text-white relative overflow-hidden" data-testid="values-section" data-nav-theme="dark">
        <div className="absolute inset-0 grid-bg-dark opacity-30"/>
        <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[var(--orange)]/10 blur-3xl"/>
        <div className="relative max-w-[1500px] mx-auto">
          <div className="reveal mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-5">Values · Vision · Mission · Standards</div>
            <h2 className="font-display text-[clamp(36px,4.6vw,68px)] leading-[1.02] font-bold tracking-tight">
              Our internal <span className="font-serif-it text-[var(--blue)]">compass.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* VALUES */}
            <div className="group relative bg-white/[0.04] border border-white/10 rounded-3xl p-9 hover:bg-white/[0.08] hover-lift transition-all duration-500 reveal">
              <div className="w-16 h-16 rounded-2xl bg-[var(--orange)]/15 flex items-center justify-center mb-7 group-hover:bg-[var(--orange)] transition-colors duration-500">
                <Heart className="w-8 h-8 text-[var(--blue)] group-hover:text-white transition-colors duration-500" strokeWidth={2}/>
              </div>
              <div className="font-display font-bold text-[26px] md:text-[28px] mb-5" style={{color:'#fff'}}>Our Values</div>
              <ul className="space-y-3 text-[14px] leading-[1.6] text-white/75">
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span><b className="text-white uppercase">Trust</b>: The foundation of every partnership and commitment we make</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span><b className="text-white uppercase">Frugality</b>: Mindful use of time, resources and capital at every step</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span><b className="text-white uppercase">Customer Centricity</b>: Anticipate, understand and exceed expectations</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span><b className="text-white uppercase">Continual Improvement</b>: Evolving processes, skills and solutions — always</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span><b className="text-white uppercase">Gratitude</b>: Appreciating every stakeholder who makes our work possible</span></li>
              </ul>
            </div>

            {/* VISION */}
            <div className="group relative bg-white/[0.04] border border-white/10 rounded-3xl p-9 hover:bg-white/[0.08] hover-lift transition-all duration-500 reveal" style={{ transitionDelay:'80ms' }}>
              <div className="w-16 h-16 rounded-2xl bg-[var(--orange)]/15 flex items-center justify-center mb-7 group-hover:bg-[var(--orange)] transition-colors duration-500">
                <Compass className="w-8 h-8 text-[var(--blue)] group-hover:text-white transition-colors duration-500" strokeWidth={2}/>
              </div>
              <div className="font-display font-bold text-[26px] md:text-[28px] mb-4" style={{color:'#fff'}}>Our Vision</div>
              <p className="text-[15px] leading-[1.85] text-white/75">
                To be a <b style={{color:'#fff'}}>technology-oriented EPC company</b> providing <b className="text-[var(--blue)]">affordable, clean and efficient energy solutions</b> through a human-centred design approach. Energizing India responsibly — one project at a time.
              </p>
            </div>

            {/* MISSION — "and" intentionally not bolded inside the highlighted phrases, "partners" replaced with "subcontractors" */}
            <div className="group relative bg-white/[0.04] border border-white/10 rounded-3xl p-9 hover:bg-white/[0.08] hover-lift transition-all duration-500 reveal" style={{ transitionDelay:'160ms' }}>
              <div className="w-16 h-16 rounded-2xl bg-[var(--orange)]/15 flex items-center justify-center mb-7 group-hover:bg-[var(--orange)] transition-colors duration-500">
                <Target className="w-8 h-8 text-[var(--blue)] group-hover:text-white transition-colors duration-500" strokeWidth={2}/>
              </div>
              <div className="font-display font-bold text-[26px] md:text-[28px] mb-4" style={{color:'#fff'}}>Our Mission</div>
              <ul className="space-y-3 text-[14px] leading-[1.7] text-white/75">
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span>Deliver exciting projects with <b className="text-white">total quality</b> and <b className="text-white">total commitment</b> which inspires national pride.</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span>Deliver quality projects within <b className="text-white">budgeted costs</b> and <b className="text-white">committed timelines</b>.</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span>Reinvest in aligning the competencies of our <b className="text-white">people</b>, <b className="text-white">products</b> and <b className="text-white">solutions</b>.</span></li>
                <li className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--orange)] shrink-0"/><span>Actively participate in the progress of all <b className="text-white">5 stakeholders</b> — teams, customers, suppliers, subcontractors, shareholders.</span></li>
              </ul>
            </div>

            {/* STANDARDS */}
            <div className="group relative bg-white/[0.04] border border-white/10 rounded-3xl p-9 hover:bg-white/[0.08] hover-lift transition-all duration-500 reveal" style={{ transitionDelay:'240ms' }}>
              <div className="w-16 h-16 rounded-2xl bg-[var(--orange)]/15 flex items-center justify-center mb-7 group-hover:bg-[var(--orange)] transition-colors duration-500">
                <Award className="w-8 h-8 text-[var(--blue)] group-hover:text-white transition-colors duration-500" strokeWidth={2}/>
              </div>
              <div className="font-display font-bold text-[26px] md:text-[28px] mb-4" style={{color:'#fff'}}>Our Standards</div>
              <p className="text-[15px] leading-[1.85] text-white/75">
                <b className="text-white">TQM-implemented</b> and certified <b className="text-[var(--blue)]">ISO 9001:2015</b>, <b className="text-[var(--blue)]">14001:2015</b> & <b className="text-[var(--blue)]">45001:2018</b> by URS, India since 2017. Every project executed to international quality, environmental, and safety benchmarks — no exceptions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MOTTO — high-quality Devanagari TEXT (renders crisp at any size) */}
      <section id="motto" className="relative overflow-hidden px-6 md:px-10 lg:px-14 py-24 lg:py-28 bg-[var(--cream-deep)]" data-testid="motto-section">
        <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none"/>
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--orange)]/8 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[var(--orange)]/6 blur-3xl pointer-events-none"/>

        <div className="relative max-w-[1200px] mx-auto text-center reveal">
          <div className="text-[11px] uppercase tracking-[0.36em] text-[var(--orange)] font-semibold mb-10">Our Core Guiding Principle</div>
          {/* Sanskrit text rendered with Noto Serif Devanagari — pixel-perfect at any zoom */}
          <h2
            lang="sa"
            className="text-[var(--orange)] font-bold leading-[1.4] mb-10 floaty"
            style={{
              fontFamily: "'Noto Serif Devanagari', serif",
              fontSize: 'clamp(48px, 8vw, 116px)',
              letterSpacing: '0.01em',
            }}
            data-testid="sanskrit-motto"
          >
            योगः कर्मसु कौशलम्
          </h2>
          <p style={{ fontFamily: "'Manrope', sans-serif", fontStyle: 'italic', fontWeight: 600, letterSpacing: '-0.02em' }} className="text-[clamp(26px,3vw,40px)] leading-[1.3] text-[var(--blue)] max-w-3xl mx-auto" data-testid="motto-tagline">
            Excellence in every action.
          </p>
          <p className="mt-7 text-[15px] md:text-[16px] text-[var(--text-mid)] max-w-2xl mx-auto leading-[1.85]">
            A timeless verse from the <b className="text-[var(--ink)]">Bhagavad Gita</b> that anchors how we work — disciplined craft, ethical execution, and meaningful outcomes for every project we undertake.
          </p>
        </div>
      </section>

      {/* OUR PROMISE / PLEDGE — refined editorial layout */}
      <section id="pledge" className="px-6 md:px-10 lg:px-14 py-24 lg:py-32 bg-[var(--cream)] relative overflow-hidden" data-testid="pledge-section">
        <div className="absolute -top-40 right-1/4 w-[420px] h-[420px] rounded-full bg-[var(--orange)]/6 blur-3xl pointer-events-none"/>

        <div className="relative max-w-[1400px] mx-auto">
          <div className="reveal mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-5">Sankalp</div>
            <h2 className="font-display text-[clamp(36px,4.6vw,68px)] leading-[1.02] font-bold tracking-tight">
              Tricolite Energy <span className="font-serif-it text-[var(--blue)]">Pledge.</span>
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[var(--text-mid)] leading-[1.85] max-w-2xl">
              Seven lines we take to heart. The values that shape how every Tricolite team member shows up — for our colleagues, our clients, and our country.
            </p>
          </div>

          {/* Editorial pledge — alternating left/right reveal with big numerals */}
          <div className="relative pl-8 md:pl-0 border-l-2 md:border-l-0 border-[var(--orange)]/30">
            <div className="grid gap-y-10 md:gap-y-12">
              {PLEDGE.map((line, i) => {
                const isOdd = i % 2 === 1;
                return (
                  <div key={i} className={`group reveal grid md:grid-cols-12 gap-5 md:gap-8 items-start ${isOdd ? 'md:pl-32 lg:pl-48' : 'md:pr-32 lg:pr-48'}`} style={{ transitionDelay: `${i*60}ms` }}>
                    <div className="md:col-span-2 flex items-start">
                      <span className="font-display font-bold text-[clamp(56px,7vw,96px)] leading-[0.8] text-[var(--blue)]/85 group-hover:text-[var(--blue)] transition-colors">{String(i+1).padStart(2,'0')}</span>
                    </div>
                    <div className="md:col-span-10 md:pt-5">
                      <p className="font-display font-medium text-[clamp(18px,1.9vw,26px)] leading-[1.45] text-[var(--ink)]">
                        {line.text}<span className="font-medium group-hover:font-bold group-hover:text-[var(--blue)] transition-colors duration-300">{line.hl}</span>{line.after}
                      </p>
                      <div className="mt-4 h-px w-12 bg-[var(--orange)]/40 group-hover:w-24 group-hover:bg-[var(--orange)] transition-all duration-500"/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP — DARK theme, compact side-by-side, fits one viewport */}
      <section id="leadership" className="px-6 md:px-10 lg:px-14 py-20 lg:py-24 bg-[var(--ink)] text-white relative overflow-hidden" data-testid="leadership-section" data-nav-theme="dark">
        <div className="absolute inset-0 grid-bg-dark opacity-25"/>
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[var(--orange)]/12 blur-3xl"/>
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-[var(--blue)]/10 blur-3xl"/>

        <div className="relative max-w-[1500px] mx-auto">
          <div className="reveal mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-4">Leadership Philosophy</div>
              <h2 className="font-display text-[clamp(32px,4vw,54px)] leading-[1.04] font-bold tracking-tight">
                Message from the <span className="font-serif-it text-[var(--blue)]">MD.</span>
              </h2>
            </div>
          </div>

          {/* Side-by-side: portrait card + quote */}
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
            <div className="lg:col-span-5 reveal">
              <div className="relative aspect-[5/6] rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[var(--blue-soft)] via-[var(--blue)] to-[var(--blue-deep)] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)]">
                <img src="/team/arn.png" alt="Abhishek Nangia, MD" className="absolute inset-0 w-full h-full object-cover object-top"/>
                <div className="absolute top-5 left-5 px-3.5 py-1.5 rounded-full bg-white text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--ink)]">MD</div>
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/70 to-transparent">
                  <div className="font-display font-bold text-[28px] md:text-[32px] leading-tight" style={{color:'#fff'}}>Abhishek Nangia</div>
                  <div className="text-[12px] text-white/75 mt-1.5 uppercase tracking-[0.2em]">MD</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 reveal">
              <div className="relative bg-white/[0.04] border border-white/10 rounded-3xl p-7 md:p-9 lg:p-10 h-full">
                <Quote className="absolute -bottom-5 -left-5 w-32 h-32 text-[var(--blue)]/12 pointer-events-none rotate-180"/>
                <p className="text-[14.5px] md:text-[16px] text-white/85 leading-[1.85]">
                  No endeavour is worthy or unworthy, <b style={{color:'#fff'}}>the mindset of the person makes it so</b>. To make swift and significant progress, every task must be taken up with <b className="text-[var(--blue)]">sincerity</b>, practiced with <b className="text-[var(--blue)]">humility</b> and completed with <b className="text-[var(--blue)]">integrity</b>. When a challenge is undertaken with <b style={{color:'#fff'}}>courage and commitment in the heart</b> coupled with <b style={{color:'#fff'}}>curiosity and calmness in the head</b>, it delivers the qualities needed for success in every participant.
                </p>
                <p className="mt-5 text-[14.5px] md:text-[16px] text-white/85 leading-[1.85]">
                  Extraordinary things become achievable when <b style={{color:'#fff'}}>determined, dexterous and disciplined</b> individuals come together as a team. At <b className="text-[var(--blue)]">TRICOLITE ENERGY</b> we strive to co-create a culture that is <b style={{color:'#fff'}}>kind and caring</b> towards our planet and people — with <b className="text-[var(--blue)]">affordable, clean and efficient</b> electrical engineering solutions.
                </p>
                <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <div className="font-serif-it text-[20px] text-[var(--blue)]">— Abhishek Nangia</div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/55">MD</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXECUTIVE TEAM — box card design with centered faces, world-class polish */}
      <section id="team" className="px-6 md:px-10 lg:px-14 py-24 lg:py-32 bg-[var(--cream)]" data-testid="team-section">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-5">Core Team</div>
            <h2 className="font-display text-[clamp(36px,4.6vw,64px)] leading-[1.02] font-bold tracking-tight">
              The people behind <span className="font-serif-it text-[var(--blue)]">every project.</span>
            </h2>
          </div>

          {/* Box cards: aspect-[4/5] with photo on top (centered face), info on bottom */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
            {TEAM.map((p, i) => (
              <div
                key={i}
                className="group reveal bg-white border border-[var(--line)] rounded-3xl overflow-hidden hover:border-[var(--blue)]/40 hover-lift transition-all duration-500"
                style={{ transitionDelay: `${i*60}ms` }}
                data-testid={`team-card-${p.name.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {/* Photo area — aspect ratio reserved, face centered */}
                <div className="relative aspect-[4/3] bg-gradient-to-br from-[var(--cream-deep)] to-[var(--cream)] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover object-[center_22%] transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-white/40 transition-all duration-500"/>
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[var(--ink)]/35 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"/>
                </div>

                {/* Info — bottom block */}
                <div className="p-6 md:p-7">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display font-bold text-[20px] md:text-[22px] leading-tight">{p.name}</h3>
                      <div className="text-[15px] uppercase tracking-wider text-[var(--blue)] mt-1.5 font-semibold">{p.role}</div>
                    </div>
                    <div className="w-9 h-9 rounded-full border border-[var(--line)] flex items-center justify-center shrink-0 group-hover:bg-[var(--blue)] group-hover:border-[var(--blue)] transition-colors duration-500">
                      <Mail className="w-4 h-4 text-[var(--text-mid)] group-hover:text-white transition-colors duration-500"/>
                    </div>
                  </div>
                  <a
                    href={`mailto:${p.email}`}
                    className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--text-mid)] hover:text-[var(--ink)] transition-colors break-all"
                    data-testid={`team-email-${p.name.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    <span className="link-u">{p.email}</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORDS FROM OUR CLIENTS — signed testimonials with bigger logos, bold phrases */}
      <section id="iso" className="px-6 md:px-10 lg:px-14 py-24 lg:py-32 bg-[var(--ink)] text-white relative overflow-hidden" data-testid="client-words-section" data-nav-theme="dark">
        <div className="absolute inset-0 grid-bg-dark opacity-25"/>
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[var(--orange)]/10 blur-3xl"/>
        <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-[var(--blue)]/10 blur-3xl"/>

        <div className="relative max-w-[1500px] mx-auto">
          <div className="reveal mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-5">Promises to our Customers</div>
            <h2 className="font-display text-[clamp(36px,4.6vw,64px)] leading-[1.02] font-bold tracking-tight">
              Promises to our customers —<br/>
              <span className="font-serif-it text-[var(--blue)]">delivered on time.</span>
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-white/75 leading-[1.85]">
              Proudly <b style={{color:'#fff'}}>ISO 9001:2015</b>, <b style={{color:'#fff'}}>ISO 14001:2015</b> & <b style={{color:'#fff'}}>ISO 45001:2018</b> certified by URS, India since 2017. But the proof shows up in what our clients put in writing.
            </p>
          </div>

          {/* Editorial testimonials — auto-shuffling, 2 visible per slide */}
          <div data-testid="about-performance-carousel-wrap">
            <PerformanceCarousel tiles={PERFORMANCE_TILES} variant="dark" />
          </div>

          {/* ISO + URS trust strip */}
          <div className="mt-14 reveal flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-[12px] uppercase tracking-[0.22em] text-white/55 border-t border-white/10 pt-10">
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[var(--blue)]"/> ISO 9001:2015 — Quality</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[var(--blue)]"/> ISO 14001:2015 — Environment</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-[var(--blue)]"/> ISO 45001:2018 — Safety</span>
            <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[var(--blue)]"/> Certified by URS · India · Since 2017</span>
          </div>
        </div>
      </section>

      {/* AREAS OF INNOVATION */}
      <section id="innovation" className="px-6 md:px-10 lg:px-14 py-24 lg:py-32 bg-[var(--cream-deep)]">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-12 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--blue)] font-semibold mb-5">Areas of Innovation</div>
            <h2 className="font-display text-[clamp(32px,4vw,56px)] leading-[1.05] font-bold tracking-tight">
              A technology-oriented <span className="font-serif-it text-[var(--blue)]">EPC company.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FOCUS_AREAS.map((f,i) => (
              <div key={i} className="group bg-white border border-[var(--line)] rounded-2xl p-7 hover-lift hover:border-[var(--orange)]/50 reveal" style={{ transitionDelay: `${i*60}ms` }}>
                <div className="flex items-start justify-between mb-6">
                  <span className="font-display font-bold text-[13px] text-[var(--blue)]">{String(i+1).padStart(2,'0')}</span>
                </div>
                <div className="font-display font-bold text-[19px] md:text-[20px] leading-snug">{f}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 lg:px-14 pb-24 bg-[var(--cream-deep)]">
        <div className="max-w-[1500px] mx-auto relative overflow-hidden rounded-3xl bg-[var(--orange)] text-white p-10 md:p-16">
          <div className="absolute inset-0 noise"/>
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="flex items-start gap-5">
              <Heart className="w-9 h-9 mt-1 shrink-0"/>
              <h2 className="font-display text-[clamp(26px,3.2vw,42px)] leading-[1.1] font-bold max-w-3xl" style={{color:'#fff'}}>Energizing India for generations.</h2>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[var(--ink)] hover:bg-black text-white px-7 py-4 rounded-full font-medium transition-colors shrink-0" data-testid="about-cta">
              Talk to Our Team <ArrowUpRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
